import React, { useState } from 'react';
import { Mail, Copy, UserPlus, Send, X, AlertCircle } from 'lucide-react';

interface TeamInvitationContentProps {
  teamName: string;
  subdomain: string;
  userName: string;
  suggestedEmails?: string[];
}

export const TeamInvitationContent: React.FC<TeamInvitationContentProps> = ({
  teamName,
  subdomain,
  userName,
  suggestedEmails = [],
}) => {
  const [emailInput, setEmailInput] = useState('');
  const [inviteeEmails, setInviteeEmails] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const addEmail = () => {
    const email = emailInput.trim();
    if (!email || !isValidEmail(email) || inviteeEmails.includes(email)) return;

    setInviteeEmails([...inviteeEmails, email]);
    setEmailInput('');
  };

  const removeEmail = (emailToRemove: string) => {
    setInviteeEmails(inviteeEmails.filter((email) => email !== emailToRemove));
  };

  const emailSubject = `Join us on PagerDuty - ${teamName} is all set up!`;
  const emailBody = `Hi there!

I just finished setting up our team on PagerDuty using the Onboarding Assistant, and I wanted to invite you to join us.

**What is PagerDuty?**
PagerDuty helps us manage incidents, on-call schedules, and escalations so our team can respond to issues quickly and effectively.

**How to get started:**
1. Visit: https://${subdomain}.pagerduty.com
2. Log in with your company credentials
3. Or use the Onboarding Assistant: https://onboarding-assistant.pagerduty.com

**Our team is ready:**
- Team: ${teamName}
- We've already configured schedules, escalation policies, and integrations

Looking forward to working with you on PagerDuty!

Best,
${userName}

---
🤖 Generated with PagerDuty Onboarding Assistant`;

  const sendInvitations = () => {
    if (inviteeEmails.length === 0) return;
    setIsSending(true);
    setShowFallback(false);

    const recipients = inviteeEmails.join(',');
    const mailtoLink = `mailto:${recipients}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;

    // Try mailto first
    window.location.href = mailtoLink;

    // Show fallback after 3 seconds if mailto didn't work
    setTimeout(() => {
      setIsSending(false);
      setShowFallback(true);
    }, 3000);
  };

  const openInGmail = () => {
    const recipients = inviteeEmails.join(',');
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(recipients)}&su=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(gmailUrl, '_blank');
  };

  const openInOutlook = () => {
    const recipients = inviteeEmails.join(',');
    const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=${encodeURIComponent(recipients)}&subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
    window.open(outlookUrl, '_blank');
  };

  const copyEmailsToClipboard = () => {
    const emailList = inviteeEmails.join(', ');
    navigator.clipboard.writeText(emailList);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(emailBody);
  };


  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: '13px', color: '#4a5568' }}>
          Invite your co-workers to join <strong>{teamName}</strong> on PagerDuty. Add their email addresses below and send invitations directly, or copy the template to customize it yourself.
        </div>
      </div>

      {/* Email Input Section */}
      <div
        style={{
          backgroundColor: '#ebf8ff',
          border: '1px solid #bee3f8',
          borderRadius: '8px',
          padding: '12px',
        }}
      >
        <div style={{ marginBottom: '8px' }}>
          <label htmlFor="coworker-email" style={{ fontSize: '13px', fontWeight: 600, color: '#2c5282', display: 'block', marginBottom: '6px' }}>
            Add Co-workers
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              id="coworker-email"
              type="email"
              autoComplete="email"
              placeholder="colleague@example.com"
              value={emailInput}
              required
              aria-required="true"
              aria-describedby="coworker-email-hint"
              aria-invalid={emailInput.trim().length > 0 && !isValidEmail(emailInput) ? 'true' : undefined}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEmail())}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #bee3f8',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: '#ffffff',
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#3182ce')}
              onBlur={(e) => (e.currentTarget.style.borderColor = '#bee3f8')}
            />
            <button
              onClick={addEmail}
              disabled={!emailInput.trim()}
              style={{
                padding: '8px 16px',
                backgroundColor: emailInput.trim() ? '#3182ce' : '#718096',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: emailInput.trim() ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => emailInput.trim() && (e.currentTarget.style.backgroundColor = '#2c5282')}
              onMouseLeave={(e) => emailInput.trim() && (e.currentTarget.style.backgroundColor = '#3182ce')}
            >
              <UserPlus size={14} />
              Add
            </button>
          </div>
          <div id="coworker-email-hint" style={{ fontSize: '11px', color: '#2c5282', marginTop: '4px' }}>
            Press Enter or click Add to include an email
          </div>
          {emailInput.trim().length > 0 && !isValidEmail(emailInput) && (
            <div role="alert" style={{ fontSize: '11px', color: '#c53030', marginTop: '4px' }}>
              Please enter a valid email address, like name@example.com.
            </div>
          )}

          {/* Suggested Emails - Gmail style */}
          {suggestedEmails.length > 0 && suggestedEmails.filter(email => !inviteeEmails.includes(email)).length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ fontSize: '11px', color: '#718096', marginBottom: '4px' }}>
                Suggested:
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {suggestedEmails
                  .filter((email) => !inviteeEmails.includes(email))
                  .map((email) => (
                    <button
                      key={email}
                      onClick={() => setInviteeEmails([...inviteeEmails, email])}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: 'transparent',
                        border: '1px solid #cbd5e0',
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: '#4a5568',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#ebf8ff';
                        e.currentTarget.style.borderColor = '#3182ce';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.borderColor = '#cbd5e0';
                      }}
                    >
                      {email}
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>

        {/* Recipients List */}
        {inviteeEmails.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#2c5282', marginBottom: '6px' }}>
              Recipients ({inviteeEmails.length}):
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {inviteeEmails.map((email) => (
                <span
                  key={email}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '4px 10px',
                    backgroundColor: '#3182ce',
                    color: '#ffffff',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                >
                  {email}
                  <button
                    aria-label={`Remove ${email}`}
                    onClick={() => removeEmail(email)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#ffffff',
                      cursor: 'pointer',
                      padding: '0',
                      fontSize: '16px',
                      lineHeight: '1',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Send Invitations Button */}
      {inviteeEmails.length > 0 && (
        <>
          <button
            onClick={sendInvitations}
            disabled={isSending}
            style={{
              padding: '12px 20px',
              backgroundColor: isSending ? '#9ae6b4' : '#38a169',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: isSending ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              width: '100%',
              opacity: isSending ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => !isSending && (e.currentTarget.style.opacity = '0.85')}
            onMouseLeave={(e) => !isSending && (e.currentTarget.style.opacity = '1')}
          >
            <Send size={16} />
            {isSending ? 'Opening email client...' : `Send ${inviteeEmails.length} Invitation${inviteeEmails.length > 1 ? 's' : ''}`}
          </button>

          {/* Fallback message if mailto didn't work */}
          {showFallback && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              padding: '12px',
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-start',
            }}>
              <AlertCircle size={16} color="#856404" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#856404', marginBottom: '4px' }}>
                  No email client found?
                </div>
                <div style={{ fontSize: '12px', color: '#856404', marginBottom: '8px' }}>
                  Try these web-based options:
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <button
                    onClick={openInGmail}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ffffff',
                      color: '#d32f2f',
                      border: '1px solid #d32f2f',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ffebee')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                  >
                    Open in Gmail
                  </button>
                  <button
                    onClick={openInOutlook}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ffffff',
                      color: '#0078d4',
                      border: '1px solid #0078d4',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e3f2fd')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                  >
                    Open in Outlook
                  </button>
                  <button
                    onClick={copyEmailsToClipboard}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ffffff',
                      color: '#424242',
                      border: '1px solid #424242',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
                  >
                    <Copy size={12} />
                    Copy Emails
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Divider */}
      {inviteeEmails.length > 0 && (
        <div style={{ position: 'relative', margin: '8px 0' }}>
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '1px', backgroundColor: '#bee3f8' }} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span style={{ fontSize: '11px', fontWeight: 500, color: '#2c5282', backgroundColor: '#ffffff', padding: '4px 12px', position: 'relative' }}>
              Or use these options
            </span>
          </div>
        </div>
      )}

      {/* Email Preview */}
      <div>
        <label htmlFor="email-preview" style={{ fontSize: '12px', fontWeight: 600, color: '#2d3748', display: 'block', marginBottom: '6px' }}>
          Email Preview:
        </label>
        <textarea
          id="email-preview"
          value={emailBody}
          readOnly
          rows={10}
          style={{
            width: '100%',
            padding: '10px',
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#ffffff',
            border: '1px solid #cbd5e0',
            borderRadius: '6px',
            color: '#2d3748',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          onFocus={(e) => (e.currentTarget.style.outline = '2px solid #3182ce')}
          onBlur={(e) => (e.currentTarget.style.outline = 'none')}
        />
      </div>

      {/* Quick Actions */}
      <div>
        <div style={{ fontSize: '12px', fontWeight: 600, color: '#2d3748', marginBottom: '8px' }}>
          {inviteeEmails.length > 0 ? 'Alternative options:' : 'Quick actions:'}
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={openInGmail}
            style={{
              flex: 1,
              minWidth: '120px',
              padding: '10px 16px',
              backgroundColor: '#ffffff',
              color: '#d32f2f',
              border: '1px solid #d32f2f',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ffebee')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            <Mail size={14} />
            Gmail
          </button>
          <button
            onClick={openInOutlook}
            style={{
              flex: 1,
              minWidth: '120px',
              padding: '10px 16px',
              backgroundColor: '#ffffff',
              color: '#0078d4',
              border: '1px solid #0078d4',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e3f2fd')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            <Mail size={14} />
            Outlook
          </button>
          <button
            onClick={copyToClipboard}
            style={{
              flex: 1,
              minWidth: '120px',
              padding: '10px 16px',
              backgroundColor: '#ffffff',
              color: '#3182ce',
              border: '1px solid #3182ce',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#ebf8ff')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            <Copy size={14} />
            Copy Template
          </button>
        </div>
      </div>

      {/* Tip */}
      <div style={{ backgroundColor: '#fffff0', border: '1px solid #fef08a', borderRadius: '6px', padding: '10px' }}>
        <div style={{ fontSize: '12px', color: '#78350f' }}>
          💡 <strong>Tip:</strong> Customize the email with specific details about your team's setup, schedules, or any onboarding instructions specific to your organization.
        </div>
      </div>
    </div>
  );
};
